import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import ToggleButton from 'components/atoms/ToggleButton';
import { bindActionCreators, Dispatch } from 'redux';
import { filterCreate, filterUpdate } from 'services/filter';
import { SetErrorHandlerAction, setErrorHandlerAction } from 'store/actions/errorsActions';
import { PortType, RootState } from 'types';
import { FavouritControlsInterface, MainControlsInterface } from 'types/functions';
import { FilterObjectType } from 'types/search';
import { ActionsType } from 'types/system';
import { checkDifferent, clone, renderError, setKey } from 'utils/formHandlers';
import { formatServerErrors } from 'utils/formatServerErrors';
import { translate } from 'utils/translate';
import validation from 'utils/validation';
import Message from 'components/atoms/Message';

interface SaveSearchModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    id?: string;
    data: any;
    actions: ActionsType | undefined;
    highlightObject: FilterObjectType;
    searchObject: FilterObjectType;
    favouritControls: FavouritControlsInterface;
    port: PortType;
    setErrorHandlerAction: SetErrorHandlerAction;
    mainControls: MainControlsInterface;
}

interface SaveSearchModelState {
    errors: object;
    data: any | null;
    loading: boolean;
    sourceSearchObject: FilterObjectType;
    overwite: boolean;
    valid: boolean;
}

class SaveSearchModel extends Component<SaveSearchModelProps, SaveSearchModelState> {
    constructor(props: SaveSearchModelProps) {
        super(props);
        const sourceSearchObject = props.mainControls.createSearchObject(false);
        this.state = {
            data: {
                type: 'user',
                home: 'false',
                filter: clone(props.searchObject),
                highlight: {
                    ...clone(props.highlightObject),
                    status:
                        checkDifferent(this.props.highlightObject, this.props.searchObject) &&
                        checkDifferent(this.props.highlightObject, sourceSearchObject),
                },
            },
            valid: true,
            errors: {},
            loading: false,
            overwite: false,
            sourceSearchObject: sourceSearchObject,
        };
    }

    componentDidMount(): void {
        const lodedFilter = this.props.favouritControls.getLodedFilter();
        if (lodedFilter) {
            this.setState(
                {
                    data: {
                        ...lodedFilter,
                        filter: clone(this.props.searchObject),
                        highlight: {
                            ...clone(this.props.highlightObject),
                            status:
                                checkDifferent(this.props.highlightObject, this.props.searchObject) &&
                                checkDifferent(this.props.highlightObject, this.state.sourceSearchObject),
                        },
                    },
                    overwite: true,
                },
                this.validate
            );
        } else {
            this.validate();
        }
    }

    validate = () => {
        const getDateFilterType = (x: any) => {
            const range = x.date.dateRange;

            const dynamicRanges = new Set([
                'lastDays',
                'today',
                'yesterday',
                'thisWeek',
                'lastWeek',
                'thisMonth',
                'lastMonth',
                'thisYear',
                'lastYear',
            ]);

            if (dynamicRanges.has(range)) {
                return 'dynamic';
            }

            if (range !== '') {
                return 'static';
            }

            if (x.date.start_utc.op != null || x.date.end_utc.op != null) {
                return 'custom';
            }

            return 'none';
        };

        const { highlight, filter } = this.state.data;

        const highlightType = getDateFilterType(highlight);
        const filterType = getDateFilterType(filter);

        const valid =
            (highlightType === 'static' && ['static', 'none'].includes(filterType)) ||
            (highlightType === 'dynamic' && ['dynamic', 'none'].includes(filterType)) ||
            (highlightType === 'custom' && ['custom', 'none'].includes(filterType)) ||
            (highlightType === 'none' && ['none', 'static', 'dynamic', 'custom'].includes(filterType));

        this.setState({
            valid: valid,
        });
    };

    handleChange = (name: string, value: unknown) => {
        const { data, errors } = this.state;
        setKey(data, name, value);
        setKey(errors, name, null);
        this.setState({ data, errors });
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.setState({ errors: {}, loading: true }, () => {
            const validationClass = new validation([{ name: 'name', rules: ['required'] }]);
            const { errors, status } = validationClass.check(this.state.data);
            if (status) {
                this.setState({ errors, loading: false });
                return;
            }

            if (this.state.data.id && this.state.overwite) {
                filterUpdate(this.props.port.id, this.state.data.id, this.state.data)
                    .then(() => {
                        this.props.favouritControls.loadSavedFilters(this.state.data.id);
                        this.props.actions?.onCancel();
                    })
                    .catch((errors) => {
                        switch (errors.response?.status) {
                            case 422:
                                this.setState({ errors: formatServerErrors(errors), loading: false });
                                break;
                            default:
                                this.setState({ loading: false });
                                this.props.setErrorHandlerAction(errors);
                                break;
                        }

                        return;
                    });
            } else {
                filterCreate(this.props.port.id, { ...this.state.data, id: null })
                    .then((res) => {
                        this.props.favouritControls.loadSavedFilters(res.items.id);
                        this.props.actions?.onCancel();
                    })
                    .catch((errors) => {
                        switch (errors.response?.status) {
                            case 422:
                                this.setState({ errors: formatServerErrors(errors), loading: false });
                                break;
                            default:
                                this.setState({ loading: false });
                                this.props.setErrorHandlerAction(errors);
                                break;
                        }

                        return;
                    });
            }
        });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded absolute">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Save this Search')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>

                    {!this.state.valid && (
                        <Message type={'error'}>
                            {translate(
                                'This search cannot be saved because it mixes dynamic and fixed date filters. Saved searches must use a consistent date filter type to remain accurate.'
                            )}
                        </Message>
                    )}

                    <FormGroup comments={renderError(this.state.errors?.['name'])} required={false}>
                        <Label>{translate('Save as')}</Label>
                        <Input
                            name="name"
                            placeholder={translate('Search name')}
                            value={this.state?.data?.name}
                            onChange={this.handleChange}
                        />
                    </FormGroup>

                    <div className="mt-5 flex items-center gap-1">
                        <div className="flex items-center align-middle">
                            <ToggleButton
                                onChange={(name, value) => {
                                    //@ts-ignore
                                    this.handleChange(name, value ? 'system' : 'user');
                                }}
                                name="type"
                                value={this.state.data.type === 'system'}
                            ></ToggleButton>
                        </div>
                        <div className="flex items-center gap-2  ">
                            <div>{translate('make this search available for all users')}</div>
                            <Icon className="w-4 h-4">
                                <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                                <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
                            </Icon>
                        </div>
                    </div>

                    {this.state.data.id && (
                        <div className="mt-5 flex items-center gap-1">
                            <div className="flex items-center align-middle">
                                <ToggleButton
                                    onChange={(name, value) => {
                                        this.setState({
                                            overwite: value,
                                        });
                                    }}
                                    name="overwite"
                                    value={this.state.overwite}
                                ></ToggleButton>
                            </div>
                            <div>{translate('Replace existing search ')}</div>
                        </div>
                    )}

                    {/* <div className="mt-5 flex items-center gap-1">
                        <div className="flex items-center align-middle">
                            <ToggleButton
                                onChange={(name, value) => {
                                    //@ts-ignore
                                    this.handleChange(name, value ? 'true' : 'false');
                                }}
                                name="home"
                                value={this.state.data.home === 'true'}
                            ></ToggleButton>
                        </div>
                        <div>{translate('Use as my login search')}</div>
                    </div> */}

                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button
                                disabled={this.state.loading || !this.state.valid}
                                loading={this.state.loading}
                                type="submit"
                                className="px-4 py-2 bg-primary-submit rounded-md text-white"
                            >
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const saveSearchModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    port: state.port.port,
    searchObject: state.layout.searchObject,
    highlightObject: state.layout.highlightObject,
    favouritControls: state.layout.favouritControls,
    mainControls: state.layout.mainControls,
});

const saveSearchModelDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(saveSearchModelStateToProps, saveSearchModelDispatchToProps)(SaveSearchModel);
